// src/sim/perception/sightability.ts — P3 FROZEN CONTRACT for P4 perception.ts (HIDE-05, D-23, D-24).
//
// FROZEN CONTRACT (D-23 verbatim): sightabilityOf(entity, mapData): number.
// P4 perception.ts calls this with only the entity and the map. Profile
// resolution is INTERNAL — we read entity.characterId, call getProfile(),
// and use that profile's hideSkill. Do NOT add a profile parameter; D-23
// locked this shape specifically to de-risk P4 (BLOCKER-2 fix).
//
// Invariants:
//   - Pure: returns a number in [0, 1]; no side effects; inputs never mutated.
//   - Fail-safe on missing profile: unknown characterId → returns 1.0 (fully
//     visible) rather than throwing, so a data-shape regression (e.g. a stale
//     characterId serialised into a save) cannot crash the P4 perception tick
//     loop. Covered by Test 11 in the unit suite.
//   - Fail-safe on unresolved spot: hidingSpotId that does not resolve against
//     mapData.hidingSpots → 1.0. Same rationale as above.
//   - Derivation (D-23): `base = 1 - profile.hideSkill * spot.skillMod`.
//     Peeking adds `P3_PEEK_VISIBILITY_PENALTY` (D-24). Clamped to [0, 1].
//   - Free / caught / escorting / spectating all return 1.0 — those statuses
//     are either "not hiding" or "visibly on the hunter's side" (escorting).
//
// Source: .planning/phases/03-core-sim-primitives/03-CONTEXT.md D-23, D-24.

import type { Entity } from '../../shared/types/world';
import type { HidingSpotAuth, MapData } from '../../shared/types/map';
import { getProfile, hasProfile } from '../characters/profiles';
import { P3_PEEK_VISIBILITY_PENALTY } from '../hiding/constants';

/**
 * Return the entity's sightability (0 = invisible, 1 = fully visible) given
 * the current map data. Profile lookup is INTERNAL — the 2-arg signature is
 * part of the D-23 frozen contract with P4 perception.ts.
 */
export function sightabilityOf(entity: Entity, mapData: MapData): number {
  // Non-hiding statuses: always fully visible. 'spectating' is reserved for P7
  // but still returns 1.0 so an accidental perception tick over a spectator
  // never under-reports.
  if (
    entity.status === 'free' ||
    entity.status === 'caught' ||
    entity.status === 'escorting' ||
    entity.status === 'spectating'
  ) {
    return 1.0;
  }

  if (entity.status === 'hiding' || entity.status === 'peeking') {
    // Fail-safe: unknown characterId (e.g. stale save with a renamed profile)
    // returns 1.0 instead of throwing from getProfile.
    if (!hasProfile(entity.characterId)) return 1.0;
    const profile = getProfile(entity.characterId);

    // Prefer floorSpot when present (on-floor hiding has no Tiled spot entry).
    // Otherwise resolve hidingSpotId against mapData.hidingSpots.
    let spot: HidingSpotAuth | undefined;
    if (entity.floorSpot !== null) {
      spot = entity.floorSpot;
    } else if (entity.hidingSpotId !== null) {
      spot = mapData.hidingSpots.find((s) => s.id === entity.hidingSpotId);
    }
    if (!spot) return 1.0;

    let s = 1 - profile.hideSkill * spot.skillMod;
    if (entity.status === 'peeking') s += P3_PEEK_VISIBILITY_PENALTY;
    return Math.max(0, Math.min(1, s));
  }

  // Exhaustive-in-practice fallback; any future EntityStatus addition lands
  // here until its sightability is defined.
  return 1.0;
}
